import { UserRole } from '@/gql/enums';
import type { GroupListItemDataFragment, MemberFragment, SubgroupListItemDataFragment } from '@/gql/graphql';

const getRolePriority = (member: MemberFragment): number => {
    if (member.__typename !== 'TenantUser' || !member.roleAssignments?.length) {
        return 999; // Unknown role goes last
    }

    if (member.roleAssignments.some(ra => ra.role === UserRole.Trainer)) {
        return 1; // Trainer has highest priority
    }

    if (member.roleAssignments.some(ra => ra.role === UserRole.Trainee)) {
        return 2; // Trainee has second priority
    }

    return 999;
};

/**
 * Sort comparator to order members by role priority.
 * Order: Trainer > Trainee
 */
export const sortMembersByRole = (a: MemberFragment, b: MemberFragment): number => {
    return getRolePriority(a) - getRolePriority(b);
};

/**
 * Calculates unassigned members in a group.
 *
 * Business Logic:
 * - Unassigned members are members who belong to the main group but are NOT assigned to any subgroup
 * - This calculation only makes sense when viewing the main group (not a specific subgroup)
 * - If there are no subgroups, all group members are considered unassigned
 * - If subgroups exist, unassigned members = all group members minus members assigned to any subgroup
 *
 * @param group - The group containing all members and subgroups. Can be undefined.
 * @param subgroup - Optional subgroup being viewed. If provided, returns empty array (unassigned concept doesn't apply).
 * @returns Array of unassigned members, sorted with trainers/admins first, or empty array if:
 *   - No group data available
 *   - Viewing a specific subgroup (unassigned concept doesn't apply in this context)
 */
export const calculateUnassignedMembers = (
    group: GroupListItemDataFragment | undefined,
    subgroup: SubgroupListItemDataFragment | undefined
): MemberFragment[] => {
    // Early return: no group data or viewing specific subgroup
    if (!group || subgroup) {
        return [];
    }

    // If no subgroups exist, all group members are unassigned
    if (!group.subgroups || group.subgroups.length === 0) {
        return [...group.allMembers].sort(sortMembersByRole);
    }

    // Collect all member IDs that are assigned to at least one subgroup
    const assignedMemberIds = new Set<string>();
    group.subgroups.forEach(sg => {
        sg.memberUserIds.forEach(id => assignedMemberIds.add(id));
    });

    // Filter out members who are assigned to any subgroup
    // Remaining members are unassigned (belong to group but not to any subgroup)
    return group.allMembers.filter(member => !assignedMemberIds.has(member.id)).sort(sortMembersByRole);
};
